import { ICloneable } from '../../interfaces/cloneable.interface';
import { CoordsUtils } from './coords.utils';
import { RectType } from './rect';

export type PointType = Point | { x: number; y: number };

export class Point implements ICloneable<Point> {
  static get empty() {
    return new Point(0, 0);
  }

  x: number;
  y: number;

  constructor(x: number, y: number) {
    this.x = x;
    this.y = y;
  }

  static fromObject(point: PointType) {
    if (point instanceof Point) {
      return point;
    }

    if (!point || (point.x === 0 && point.y === 0)) {
      return Point.empty;
    } else {
      return new Point(point.x, point.y);
    }
  }

  static fromElement(element: HTMLElement) {
    const { left, top } = element.getBoundingClientRect();

    if (left === 0 && top === 0) {
      return Point.empty;
    } else {
      return new Point(left, top);
    }
  }

  static centerFromElement(element) {
    return Point.centerFromDomRect(element.getBoundingClientRect());
  }

  static centerFromDomRect(domRect: DOMRect) {
    const { left, top, width, height } = domRect;

    if (left === 0 && top === 0 && width === 0 && height === 0) {
      return Point.empty;
    } else {
      return new Point(left + width / 2, top + height / 2);
    }
  }

  /**
   * @param {Rect} rect
   *
   * @returns {Point}
   */
  static centerFromRect(rect) {
    const { x, y, width, height } = rect;

    if (x === 0 && y === 0 && width === 0 && height === 0) {
      return Point.empty;
    } else {
      return new Point(x + width / 2, y + height / 2);
    }
  }

  isNearRectBoundaries(rect: RectType, threshold: number = 0) {
    return CoordsUtils.isPointNearRectBoundaries(this, rect, threshold);
  }

  relativeTo(point: PointType) {
    this.x -= point.x;
    this.y -= point.y;
    return this;
  }

  clone() {
    return new Point(this.x, this.y);
  }

  equals(point: PointType) {
    if (!point || typeof point !== 'object') {
      return false;
    }

    if (point === this) {
      return true;
    }

    return this.x === point.x && this.y === point.y;
  }
}
